import React from "react";
import { Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

const statusColor = {
  draft: "default",
  pending: "warning",
  approved: "info",
  active: "success",
  finished: "default",
  rejected: "error",
};

function formatDate(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function PromotionTable({
  promotions = [],
  programsById = {},
  coursesById = {},
  onSubmit,
  onApprove,
  onReject,
  onEdit,
  actionLoadingId,
  /** When false (e.g. Manager), hide Approve / Reject; create & submit remain via parent. */
  canApproveReject = true,
  showTitle = true,
  paperClassName,
  tableClassName,
}) {
  return (
    <Paper
      variant="outlined"
      className={paperClassName}
      sx={{
        p: showTitle && !paperClassName ? 2 : 0,
        overflow: "hidden",
        borderRadius: paperClassName ? "16px" : undefined,
      }}
    >
      {showTitle ? (
        <Typography variant="h6" sx={{ mb: 2, px: 2, pt: paperClassName ? 2 : 0 }}>
          Promotions
        </Typography>
      ) : null}
      <TableContainer sx={{ maxHeight: 560 }}>
        <Table size="small" stickyHeader className={tableClassName}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Discount %</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No promotions found.
                </TableCell>
              </TableRow>
            ) : (
              promotions.map((promotion) => {
                const target = promotion.course_id
                  ? `Course: ${coursesById[promotion.course_id]?.name || promotion.course_id}`
                  : `Program: ${programsById[promotion.program_id]?.name || promotion.program_id}`;
                const busy = actionLoadingId === promotion.id;
                const showSubmit = promotion.status === "draft";
                const showApprove = canApproveReject && promotion.status === "pending";
                const showReject =
                  canApproveReject && (promotion.status === "pending" || promotion.status === "approved");
                const showEdit = promotion.status === "draft" || promotion.status === "pending";
                const hasActions = showEdit || showSubmit || showApprove || showReject;
                return (
                  <TableRow key={promotion.id}>
                    <TableCell>{promotion.name}</TableCell>
                    <TableCell>{target}</TableCell>
                    <TableCell>{promotion.discount_percentage}%</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor[promotion.status] || "default"}
                        label={promotion.status}
                        variant={promotion.status === "active" ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>{formatDate(promotion.start_date)}</TableCell>
                    <TableCell>{formatDate(promotion.end_date)}</TableCell>
                    <TableCell align="right">
                      {hasActions ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {showEdit ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => onEdit?.(promotion)}
                            >
                              Edit
                            </Button>
                          ) : null}
                          {showSubmit ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => onSubmit?.(promotion.id)}
                            >
                              Submit
                            </Button>
                          ) : null}
                          {showApprove ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={busy}
                              onClick={() => onApprove?.(promotion.id)}
                            >
                              Approve
                            </Button>
                          ) : null}
                          {showReject ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busy}
                              onClick={() => onReject?.(promotion.id)}
                            >
                              Reject
                            </Button>
                          ) : null}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

